import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  Calendar,
  Clock,
  User,
  FileText,
  Briefcase,
  AlertCircle,
  Activity,
  Brain,
} from 'lucide-react'
import { toast } from 'sonner'
import { tasksService, Task, TaskUpdate } from '../services/tasks'
import { projectsService, Project } from '../services/projects'
import { aiInsightsService, AIInsight } from '../services/aiInsights'
import { InsightCard } from '../components/ui/InsightCard'
import { CommentThread } from '../components/CommentThread'
import { ActivityFeed } from '../components/ActivityFeed'
import { AttachmentList } from '../components/AttachmentList'
import { TaskEvidencePanel } from '../components/project/TaskEvidencePanel'
import { TaskTimerPanel } from '../components/project/TaskTimerPanel'
import { UserPicker } from '../components/UserPicker'
import { usersService } from '../services/users'
import { useTranslation } from '../i18n/I18nContext'
import type { ApiErrorLike } from '../utils/apiError'

export default function TaskDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [task, setTask] = useState<Task | null>(null)
  const [project, setProject] = useState<Project | null>(null)
  const [assigneeName, setAssigneeName] = useState<string | null>(null)
  const [insights, setInsights] = useState<AIInsight[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<TaskUpdate>({})

  useEffect(() => {
    if (id) {
      fetchTask()
    }
  }, [id])

  useEffect(() => {
    if (task?.project_id) {
      fetchProject(task.project_id)
    }
  }, [task?.project_id])

  const fetchTask = async () => {
    if (!id) return
    setLoading(true)
    try {
      const data = await tasksService.get(parseInt(id))
      setTask(data)
      setFormData({
        title: data.title,
        description: data.description || '',
        status: data.status,
        priority: data.priority,
        assignee_id: data.assignee_id ?? undefined,
        due_date: data.due_date || '',
        estimated_hours: data.estimated_hours || undefined,
        actual_hours: data.actual_hours || undefined,
      })
      // Resolve assignee display name for the read-only view
      if (data.assignee_id) {
        usersService
          .get(data.assignee_id)
          .then((u) => setAssigneeName(u.full_name || u.email))
          .catch(() => setAssigneeName(null))
      } else {
        setAssigneeName(null)
      }
      // Load AI insights for the parent project.
      // Sprint 16: was silent — non-critical (insight panel just hides
      // when empty), but log so dev tools surface fetch failures.
      if (data.project_id) {
        aiInsightsService.list({ entity_type: 'project', entity_id: data.project_id, limit: 2 })
          .then(setInsights)
          .catch((err) => {
            console.warn('Failed to load AI insights for task:', err)
          })
      }
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error fetching task:', error)
      toast.error('Failed to load task')
      navigate('/task')
    } finally {
      setLoading(false)
    }
  }

  const fetchProject = async (projectId: number) => {
    try {
      const data = await projectsService.get(projectId)
      setProject(data)
    } catch (error) {
      console.error('Error fetching project:', error)
    }
  }

  const handleChange = (field: keyof TaskUpdate) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const value = e.target.value
    setFormData((prev) => {
      if (field === 'estimated_hours' || field === 'actual_hours') {
        return { ...prev, [field]: value ? parseFloat(value) : undefined }
      }
      return { ...prev, [field]: value }
    })
  }

  const handleSave = async () => {
    if (!id) return
    setSaving(true)
    try {
      const updatedTask = await tasksService.update(parseInt(id), formData)
      setTask(updatedTask)
      setEditing(false)
      toast.success('Task updated successfully')
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error updating task:', error)
      toast.error(error.response?.data?.detail || 'Failed to update task')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    if (task) {
      setFormData({
        title: task.title,
        description: task.description || '',
        status: task.status,
        priority: task.priority,
        assignee_id: task.assignee_id ?? undefined,
        due_date: task.due_date || '',
        estimated_hours: task.estimated_hours || undefined,
        actual_hours: task.actual_hours || undefined,
      })
    }
    setEditing(false)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done':
        return 'bg-green-100 text-green-700 border-green-300'
      case 'in_progress':
        return 'bg-blue-100 text-blue-700 border-blue-300'
      case 'in_review':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300'
      case 'todo':
        return 'bg-gray-100 text-gray-700 border-gray-300'
      case 'blocked':
        return 'bg-red-100 text-red-700 border-red-300'
      case 'cancelled':
        return 'bg-red-100 text-red-700 border-red-300'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-700 border-red-300'
      case 'high':
        return 'bg-orange-100 text-orange-700 border-orange-300'
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-300'
      case 'low':
        return 'bg-gray-100 text-gray-700 border-gray-300'
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300'
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#486581] border-t-transparent mb-4"></div>
          <p className="text-[#556B7C]">Loading task...</p>
        </div>
      </div>
    )
  }

  if (!task) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-lg font-medium text-red-700 mb-2">Task not found</p>
          <button
            onClick={() => navigate('/task')}
            className="px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] transition-colors"
          >
            Back to Tasks
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6 bg-[#f0f4f8] min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#102a43] via-[#486581] to-[#102a43] rounded-xl p-6 text-white shadow-lg border border-[#829ab1]/20 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/task')}
                className="p-2 hover:bg-[#486581] rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl mb-2">
                  {editing ? 'Edit Task' : task.title}
                </h1>
                {!editing && (
                  <p className="text-[#bcccdc]">Task Details</p>
                )}
              </div>
            </div>
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="px-4 py-2 bg-[#486581] hover:bg-[#5a7692] border border-[#829ab1] rounded-lg transition-colors flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
            )}
          </div>
        </div>

        {/* AI Insights */}
        {!editing && insights.length > 0 && (
          <section className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Brain className="w-4 h-4 text-[#DAA520]" />
              <h2 className="text-sm font-semibold text-[#2A3B4C]">AI Insights</h2>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {insights.map(i => (
                <InsightCard key={i.id} insight={i} onFeedback={(iid, score) => aiInsightsService.submitFeedback(iid, score)} />
              ))}
            </div>
          </section>
        )}

        {/* Main Content */}
        <div className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-8">
          {editing ? (
            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-[#102a43] mb-2">
                  Task Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title || ''}
                  onChange={handleChange('title')}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-[#102a43] mb-2">Description</label>
                <textarea
                  value={formData.description || ''}
                  onChange={handleChange('description')}
                  rows={6}
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none resize-none text-[#2A3B4C]"
                />
              </div>

              {/* Status and Priority */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#102a43] mb-2">Status</label>
                  <select
                    value={formData.status || 'todo'}
                    onChange={handleChange('status')}
                    className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                  >
                    <option value="todo">To Do</option>
                    <option value="in_progress">In Progress</option>
                    <option value="in_review">In Review</option>
                    <option value="done">Done</option>
                    <option value="blocked">Blocked</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#102a43] mb-2">Priority</label>
                  <select
                    value={formData.priority || 'medium'}
                    onChange={handleChange('priority')}
                    className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              {/* Due Date and Hours */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-[#102a43] mb-2">Due Date</label>
                  <input
                    type="date"
                    value={formData.due_date || ''}
                    onChange={handleChange('due_date')}
                    className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#102a43] mb-2">Estimated Hours</label>
                  <input
                    type="number"
                    value={formData.estimated_hours || ''}
                    onChange={handleChange('estimated_hours')}
                    min="0"
                    step="0.5"
                    className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                  />
                </div>
              </div>

              {/* Actual Hours */}
              <div>
                <label className="block text-sm font-medium text-[#102a43] mb-2">Actual Hours</label>
                <input
                  type="number"
                  value={formData.actual_hours || ''}
                  onChange={handleChange('actual_hours')}
                  min="0"
                  step="0.5"
                  className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
                />
              </div>

              {/* Assignee */}
              <div>
                <label className="block text-sm font-medium text-[#102a43] mb-2">
                  {t('task.assignee.label')}
                </label>
                <UserPicker
                  value={formData.assignee_id ?? null}
                  onChange={(userId) =>
                    setFormData((prev) => ({ ...prev, assignee_id: userId ?? undefined }))
                  }
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-4 pt-4 border-t border-[#bcccdc]">
                <button
                  onClick={handleCancel}
                  className="px-6 py-2 border-2 border-[#bcccdc] rounded-lg hover:bg-[#f0f4f8] transition-colors flex items-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !formData.title?.trim()}
                  className="px-6 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Status Badges */}
              <div className="flex flex-wrap gap-4">
                <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(task.status)}`}>
                  {task.status.replace('_', ' ')}
                </span>
                <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getPriorityColor(task.priority)}`}>
                  {task.priority} Priority
                </span>
              </div>

              {/* Description */}
              {task.description && (
                <div>
                  <h3 className="text-sm font-medium text-[#102a43] mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Description
                  </h3>
                  <p className="text-[#2A3B4C] whitespace-pre-wrap">{task.description}</p>
                </div>
              )}

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-[#bcccdc]">
                <div>
                  <h3 className="text-sm font-medium text-[#102a43] mb-2 flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Project
                  </h3>
                  <p className="text-[#2A3B4C]">
                    {project ? project.name : `Project #${task.project_id}`}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#102a43] mb-2 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {t('task.assignee.label')}
                  </h3>
                  <p className="text-[#2A3B4C]">
                    {task.assignee_id ? (assigneeName || `User #${task.assignee_id}`) : t('task.assignee.unassigned')}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#102a43] mb-2 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Due Date
                  </h3>
                  <p className="text-[#2A3B4C]">{formatDate(task.due_date)}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#102a43] mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Estimated Hours
                  </h3>
                  <p className="text-[#2A3B4C]">
                    {task.estimated_hours ? `${task.estimated_hours}h` : '-'}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#102a43] mb-2 flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Actual Hours
                  </h3>
                  <p className="text-[#2A3B4C]">
                    {task.actual_hours ? `${task.actual_hours}h` : '-'}
                  </p>
                </div>
              </div>

              {/* Timestamps */}
              <div className="pt-6 border-t border-[#bcccdc]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-[#556B7C]">
                  <div>
                    <span className="font-medium">Created:</span>{' '}
                    {new Date(task.created_at).toLocaleString()}
                  </div>
                  <div>
                    <span className="font-medium">Last Updated:</span>{' '}
                    {new Date(task.updated_at).toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Part 7 — Time tracking + evidence */}
              <div className="pt-6 border-t border-[#bcccdc] grid grid-cols-1 lg:grid-cols-2 gap-4">
                <TaskTimerPanel taskId={task.id} />
                <TaskEvidencePanel taskId={task.id} projectId={task.project_id} />
              </div>

              {/* Part 5 — Attachments, comments, activity */}
              <div className="pt-6 border-t border-[#bcccdc]">
                <h3 className="text-sm font-medium text-[#102a43] mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Files
                </h3>
                <AttachmentList
                  entityType="task"
                  entityId={task.id}
                  allowCamera={task.status !== 'done'}
                />
                {task.status === 'done' && (
                  <div className="mt-4">
                    <p className="text-xs text-[#556B7C] mb-1">
                      Photo evidence of completion
                    </p>
                    <AttachmentList
                      entityType="task"
                      entityId={task.id}
                      purpose="completion_evidence"
                      allowCamera
                    />
                  </div>
                )}
              </div>

              <div className="pt-6 border-t border-[#bcccdc] grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-[#102a43] mb-3">Comments</h3>
                  <CommentThread entityType="task" entityId={task.id} />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#102a43] mb-3">Activity</h3>
                  <ActivityFeed entityType="task" entityId={task.id} limit={30} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

